import { Box, Button, Card, createStyles, Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import { Quote } from "../../../api/Quote/Quote";
import React from "react";
import { QuoteHr } from "../../quotePage/components/QuoteHr";
import { getFirstImage, ProductSubscription } from "../../../api/Product/ProductSubscriptionApi";
import { format } from "date-fns";
import QuoteButtonAddSubscription from "../../quotePage/components/QuoteButtonAddSubscription";
import { history } from "../../../app/rootReducer";
import { SubscriptionDelayInDays } from "../../../app/consts";
import { hasSubscriptions, paymentTypeTitle } from "../../../app/utils";
import { PriceFormat } from "../../../components/typography/PriceFormat";

interface Props {
  quote: Quote;
  subscriptions?: ProductSubscription[];
  onRemoveSubscription: (subscription: ProductSubscription) => void;
  onAddSubscription: (subscription: ProductSubscription) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    image: {
      margin: "0 auto",
      display: "block",
      width: "100%",
      maxWidth: "15rem"
    }
  })
);

const QuoteCartProductSubscription = ({ quote, subscriptions, onRemoveSubscription, onAddSubscription }: Props) => {
  const classes = useStyles();

  if (!hasSubscriptions(subscriptions)) {
    return null;
  }

  const subscriptionView = function (subscription: ProductSubscription, index: number) {
    if (!subscription) {
      return null;
    }
    const selected = quote.subscriptions?.some((i) => i.productId === subscription.id) ?? false;

    const onRemoveSubscriptionItem = () => {
      if (selected && onRemoveSubscription) {
        onRemoveSubscription(subscription);
      }
    };

    const onView = function () {
      history.push("/protect");
    };

    const removeButtonSection = selected && (
      <Box textAlign="center">
        <Button size="small" onClick={onRemoveSubscriptionItem} color="secondary" variant="text">
          Remove
        </Button>
      </Box>
    );

    const addSection = selected ? (
      <Box fontWeight="fontWeightBold" textAlign="right">
        <PriceFormat value={subscription.prices[0].price} />
        &nbsp;{paymentTypeTitle(subscription.prices[0].paymentType, true)}
      </Box>
    ) : (
      <QuoteButtonAddSubscription
        quote={quote}
        subscription={subscription}
        onAddSubscription={onAddSubscription}
        fullWidth
      />
    );

    let date = new Date();
    date.setDate(date.getDate() + SubscriptionDelayInDays);
    const startDate = format(date, "do LLLL");

    const bottomSection = selected ? (
      <React.Fragment>
        <Box display="flex">
          <Box width="60%">
            <Typography variant="body1">
              We will send you confirmation once your subscription begins on{" "}
              <Box fontWeight="fontWeightBold" component="span">
                {startDate}
              </Box>
              .
            </Typography>
          </Box>
          <Box ml="auto" textAlign="center">
            {removeButtonSection}
          </Box>
        </Box>
      </React.Fragment>
    ) : (
      <React.Fragment>
        <Typography variant="body1">
          We have a cover plan that is hassle free every year and ensures your boiler is running safely and efficiently
          for only&nbsp;
          <Box fontWeight="fontWeightBold" component="span">
            <PriceFormat value={subscription.prices[0].price} />
          </Box>
          &nbsp;{paymentTypeTitle(subscription.prices[0].paymentType, false)}
        </Typography>
        <Button onClick={onView} color="secondary">
          View Details
        </Button>
      </React.Fragment>
    );

    const plusSection = selected ? (
      <Typography variant="h4" component="span">
        Plus
      </Typography>
    ) : null;

    return (
      <Box key={index}>
        <QuoteHr mt={3} mb={3} />
        <Box>
          <Grid container spacing={2}>
            <Grid item md={3} xs={4}>
              <Card square>
                <img className={classes.image} src={getFirstImage(subscription)?.url} alt={subscription.name} />
              </Card>
            </Grid>
            <Grid item md={9} xs={8}>
              <Grid container spacing={3}>
                <Grid item md={8} xs={8}>
                  {plusSection}
                  <Typography variant={selected ? "body1" : "h4"}>
                    <Box textAlign="left" fontWeight={selected ? "" : "fontWeightBold"} component="span">
                      {subscription.name}
                    </Box>
                  </Typography>
                </Grid>
                <Grid item md={4} xs={4}>
                  {addSection}
                </Grid>
                <Grid item xs={12}>
                  {bottomSection}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  return <React.Fragment>{subscriptions!.map((li, index) => subscriptionView(li, index))}</React.Fragment>;
};

export default QuoteCartProductSubscription;
